// -----------------------------------------------------------------------------------------------------
// @ AUTH UTILITIES
//
// Methods are derivations of the Auth0 Angular-JWT helper service methods
// https://github.com/auth0/angular2-jwt
// -----------------------------------------------------------------------------------------------------

export class AuthUtils {
    /**
     * Constructor
     */
    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Is token expired?
     *
     * @param token
     * @param offsetSeconds
     */
    static isTokenExpired(token: string, offsetSeconds?: number): boolean {
        // Return if there is no token
        if (!token || token === '') {
            return true;
        }

        // Get the expiration date
        const date = this._getTokenExpirationDate(token);

        offsetSeconds = offsetSeconds || 0;

        if (date === null) {
            return true;
        }

        // Check if the token is expired
        return date.valueOf() <= (new Date().valueOf() + offsetSeconds * 1000);
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Base64 decoder
     * Credits: https://github.com/atk
     *
     * @param str
     * @private
     */
    private static _b64decode(str: string): string {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=';
        let output = '';

        str = String(str).replace(/=+$/, '');

        if (str.length % 4 === 1) {
            throw new Error(
                '\'atob\' failed: The string to be decoded is not correctly encoded.'
            );
        }

        /* eslint-disable */
        let bc = 0, bs: any, buffer: any, idx = 0;

        while ((buffer = str.charAt(idx++))) {
            // try to find character in table (0-63, not found => -1)
            buffer = chars.indexOf(buffer);

            if (~buffer) {
                if (bc % 4) {
                    bs = bs * 64 + buffer;
                } else {
                    bs = buffer;
                }

                if (bc++ % 4) {
                    const charCode = 255 & (bs >> ((-2 * bc) & 6));
                    output += String.fromCharCode(charCode);
                }
            }
        }
        /* eslint-enable */

        return output;
    }

    /**
     * Base64 unicode decoder
     *
     * @param str
     * @private
     */
    private static _b64DecodeUnicode(str: any): string {
        return decodeURIComponent(
            Array.prototype.map
                .call(this._b64decode(str), (c: any) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        );
    }

    /**
     * URL Base 64 decoder
     *
     * @param str
     * @private
     */
    private static _urlBase64Decode(str: string): string {
        let output = str.replace(/-/g, '+').replace(/_/g, '/');
        switch (output.length % 4) {
            case 0:
                {
                    break;
                }
            case 2:
                {
                    output += '==';
                    break;
                }
            case 3:
                {
                    output += '=';
                    break;
                }
            default:
                {
                    throw Error('Illegal base64url string!');
                }
        }
        return this._b64DecodeUnicode(output);
    }

    /**
     * Get token expiration date
     *
     * @param token
     * @private
     */
    private static _getTokenExpirationDate(token: string): Date | null {
        // Get the decoded token
        const decodedToken = this._decodeTokenPermission(token);

        // Return if the decodedToken doesn't have an 'exp' field
        if (!decodedToken.hasOwnProperty('exp')) {
            return null;
        }

        // Convert the expiration date
        const date = new Date(0);
        date.setUTCSeconds(decodedToken.exp);

        return date;
    }


    /**
     * Decode token permission
     *
     * @param token
     * @private
     */
    private static _decodeTokenPermission(token: string): any {
        // Return if there is no token
        if (!token) {
            return null;
        }

        // Split the token
        const parts = token.split('.');

        if (parts.length !== 3) {
            throw new Error('The inspected token doesn\'t appear to be a JWT. Check to make sure it has three parts and see https://jwt.io for more.');
        }

        // Decode the token using the Base64 decoder
        const decoded = this._urlBase64Decode(parts[1]);

        if (!decoded) {
            throw new Error('Cannot decode the token.');
        }

        return JSON.parse(decoded);
    }


    /**
     * Get token user admin or not
     *
     * @param token
     * @public
     */
    public static _getTokenUser(token: string): boolean {
        // Get the decoded token
        const decodedToken = this._decodeTokenPermission(token);

        // Return if the decodedToken doesn't have an 'exp' field
        if (!decodedToken.hasOwnProperty('is_superuser')) {
            return null;
        }
        return decodedToken.is_superuser;
    }

    /**
     * Get permisos
     *
     * @public
     */
    public static obtenerPermisos() {
        const token = localStorage.getItem('permissionsToken');
        if (token) {
            const isAdmin = AuthUtils._getTokenUser(token);
            if (isAdmin) {
                return true;
            }
        } else {
            console.log('No se encontró ningún token');
        }
        return false;
    }


    /**
    * Get token Permisos rol
    *
    * @param token
    * @public
    */
    public static _getTokenPermissionRol(token: string): string[] {
        const decodedToken = this._decodeTokenPermission(token);
        if (!decodedToken.hasOwnProperty('permisos_rol')) {
            return null;
        }

        return decodedToken.permisos_rol;
    }

    public static _getTokenCreateNovedad(token: string): boolean {
        const decodedToken = this._decodeTokenPermission(token);
        const tienePermiso = decodedToken.permisos_rol.includes("permiso_solicitar_novedad");
        if (tienePermiso) {
            return true;
        }
        return false;
    }

}
